import { ImageAnnotatorContext, TOOL, useImageAnnotator } from "@msuite/image-annotator";
import { guardArray } from "@msuite/utils/array";
import { guardNumber } from "@msuite/utils/numbers";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useResizeObserver from "use-resize-observer";
import { useHotkey } from "../../hooks";
import { useTranslation } from "../../localization";
import { toast } from "../../toast";
import { downloadFile } from "../../utils";
import {
	AnnotationCanvas,
	AnnotationToolbar,
	exportAnnotatedImage,
	getAnnotatedImageFileName,
} from "../annotator";
import type { FileAnnotationsResult, GetFileAnnotations, SaveFileAnnotations } from "../shared";
import type { LightboxAnnotationControls } from "./shared";

/** Props Interface */
interface LightboxAnnotatedImageProps {
	fileId: string;
	src: string;
	fileName: string;
	getAnnotations?: GetFileAnnotations;
	onSaveAnnotations?: SaveFileAnnotations;
	onControlsChange?: (controls: LightboxAnnotationControls | null) => void;
	footerSlot?: HTMLElement | null;
}

/**
 * Computes the exact pixel size an image is rendered at when scaled to fit,
 * preserving its aspect ratio, within the given boundary box.
 */
function fitContainer(
	image: { width: number; height: number },
	boundaries: { width: number; height: number },
) {
	const imageRatio = image.width / image.height;
	const boundaryRatio = boundaries.width / boundaries.height;
	if (imageRatio > boundaryRatio) {
		return { width: boundaries.width, height: boundaries.width / imageRatio };
	}
	return { width: boundaries.height * imageRatio, height: boundaries.height };
}

/**
 * Whether a keyboard event came from somewhere text is being entered — a label input, a textarea,
 * or a rich text editor. Backspace belongs to the text there, not to the selected annotation.
 */
function isTypingTarget(target: EventTarget | null) {
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export function LightboxAnnotatedImage({
	fileId,
	src,
	fileName,
	getAnnotations,
	onSaveAnnotations,
	onControlsChange,
	footerSlot,
}: LightboxAnnotatedImageProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [canShare, setCanShare] = useState<boolean>(false);
	const [fileData, setFileData] = useState<FileAnnotationsResult | null>(null);
	const [boundarySize, setBoundarySize] = useState<{ width: number; height: number } | null>(null);

	/** Refs */
	const svgRef = useRef<SVGSVGElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const boundaryRef = useRef<HTMLDivElement>(null);

	/** Computed */
	const image =
		fileData?.file?.width && fileData?.file?.height
			? { width: guardNumber(fileData.file.width), height: guardNumber(fileData.file.height) }
			: null;
	const fitSize = image && boundarySize ? fitContainer(image, boundarySize) : null;

	/** Annotator */
	const annotator = useImageAnnotator({
		image,
		webSvgRef: svgRef,
		isReadOnly: !isEditing,
	});

	/** Functions */
	function handleOnEnterEditMode() {
		annotator.handleOnChangeTool(TOOL.SELECT);
		annotator.setSelectionId(null);
		setIsEditing(true);
	}

	function handleOnCancel() {
		annotator.setAnnotations(guardArray(fileData?.annotations?.annotations));
		annotator.handleOnChangeTool(TOOL.SELECT);
		annotator.setSelectionId(null);
		setIsEditing(false);
	}

	async function handleOnSave() {
		if (!onSaveAnnotations) return;
		try {
			setIsSaving(true);
			await onSaveAnnotations(fileId, annotator.annotations);
			setFileData((prev) =>
				prev ? { ...prev, annotations: { annotations: annotator.annotations } } : prev,
			);
			annotator.handleOnChangeTool(TOOL.SELECT);
			annotator.setSelectionId(null);
			setIsEditing(false);
		} finally {
			setIsSaving(false);
		}
	}

	/**
	 * Flattens the overlay onto the image. Returns null when the source dimensions are not known
	 * yet, because without them there is no canvas to draw into.
	 */
	async function buildExportFile(): Promise<File | null> {
		if (!image || !svgRef.current) return null;
		const blob = await exportAnnotatedImage({ src, svg: svgRef.current, image });
		return new File([blob], getAnnotatedImageFileName(fileName), { type: "image/jpeg" });
	}

	async function handleOnDownload() {
		try {
			setIsExporting(true);
			const file = await buildExportFile();
			if (!file) return;
			downloadFile(file, file.name);
		} catch {
			toast.error(t("image_annotator.export_failed"));
		} finally {
			setIsExporting(false);
		}
	}

	async function handleOnShare() {
		try {
			setIsExporting(true);
			const file = await buildExportFile();
			if (!file) return;
			if (!navigator.canShare?.({ files: [file] })) {
				toast.error(t("image_annotator.share_unavailable"));
				return;
			}
			await navigator.share({ files: [file], title: file.name });
		} catch (error) {
			/** Dismissing the share sheet rejects with AbortError — a choice, not a failure. */
			if (error instanceof DOMException && error.name === "AbortError") return;
			toast.error(t("image_annotator.export_failed"));
		} finally {
			setIsExporting(false);
		}
	}

	function handleOnDeleteHotkey(event: KeyboardEvent) {
		if (!annotator.hasSelection) return;
		if (isTypingTarget(event.target)) return;
		event.preventDefault();
		annotator.handleOnDeleteSelection();
	}

	/**
	 * The registered control callbacks must stay stable (so re-registration only happens on
	 * boolean state changes, never every render) yet always call the latest handlers — otherwise
	 * a save could persist a stale annotation snapshot. Route stable wrappers through a ref.
	 */
	const latest = useRef({
		handleOnEnterEditMode,
		handleOnCancel,
		handleOnSave,
		handleOnDownload,
		handleOnShare,
		onToggleShow: () => annotator.handleOnToggleShowAnnotations(),
	});
	latest.current = {
		handleOnEnterEditMode,
		handleOnCancel,
		handleOnSave,
		handleOnDownload,
		handleOnShare,
		onToggleShow: () => annotator.handleOnToggleShowAnnotations(),
	};
	const stableHandlers = useMemo(
		() => ({
			onEnterEdit: () => latest.current.handleOnEnterEditMode(),
			onCancel: () => latest.current.handleOnCancel(),
			onSave: () => latest.current.handleOnSave(),
			onToggleShow: () => latest.current.onToggleShow(),
			onDownload: () => latest.current.handleOnDownload(),
			onShare: () => latest.current.handleOnShare(),
		}),
		[],
	);

	/** Computed */
	const hasImage = !!image;
	// Two conditions, and they ask different things: whether this surface saves annotations at all,
	// and whether the caller's rule reaches this file. The second arrives with the file, so before
	// it does the toolbar behaves as the surface alone decides — there is no image to annotate yet.
	const canEdit = !!onSaveAnnotations && fileData?.canEdit !== false;
	const hasAnnotations = annotator.annotations.length > 0;
	const showAnnotations = annotator.showAnnotations;

	/** Effects */
	useResizeObserver({
		ref: boundaryRef as React.RefObject<HTMLDivElement>,
		onResize: ({ width, height }) => {
			if (!width || !height) return;
			// Freeze the boundary at its first valid measurement so entering edit mode
			// (which swaps the footer contents) does not rescale the image.
			setBoundarySize((prev) => prev ?? { width, height });
		},
	});

	/**
	 * Whether files can be shared is only answerable by offering `canShare` a real file — the
	 * presence of `navigator.share` says nothing about file support, which most desktop browsers
	 * lack. Probing once at mount keeps the button off the toolbar where it would never work.
	 */
	useEffect(() => {
		const probe = new File([""], "probe.jpg", { type: "image/jpeg" });
		setCanShare(Boolean(navigator.canShare?.({ files: [probe] })));
	}, []);

	/**
	 * A file's saved annotations are loaded once, when the file changes, and the load overwrites
	 * whatever is on the canvas. `getAnnotations` therefore must not be a dependency: call sites
	 * pass an inline closure, so its identity is new on every render of the surface above — a
	 * permission refetch on window focus is enough to produce one — and a re-run would replace the
	 * annotations drawn since with the ones last saved. The ref keeps the effect keyed to `fileId`
	 * while still calling the current callback.
	 */
	const getAnnotationsRef = useRef(getAnnotations);
	getAnnotationsRef.current = getAnnotations;

	useEffect(() => {
		const loadAnnotations = getAnnotationsRef.current;
		if (!loadAnnotations) return;
		let isCancelled = false;

		loadAnnotations(fileId).then((data) => {
			if (isCancelled) return;
			setFileData(data);
			annotator.setAnnotations(guardArray(data.annotations?.annotations));
		});

		return () => {
			isCancelled = true;
		};
	}, [fileId]);

	useEffect(() => {
		if (!hasImage) {
			onControlsChange?.(null);
			return;
		}
		onControlsChange?.({
			canEdit,
			isEditing,
			isSaving,
			isExporting,
			hasAnnotations,
			showAnnotations,
			canShare,
			...stableHandlers,
		});
	}, [
		hasImage,
		canEdit,
		isEditing,
		isSaving,
		isExporting,
		hasAnnotations,
		showAnnotations,
		canShare,
		stableHandlers,
		onControlsChange,
	]);

	useEffect(() => {
		return () => onControlsChange?.(null);
	}, [onControlsChange]);

	/** Hotkey */
	useHotkey("Backspace", handleOnDeleteHotkey, { enabled: isEditing });
	useHotkey("Delete", handleOnDeleteHotkey, { enabled: isEditing });

	/** Render */
	return (
		<ImageAnnotatorContext.Provider value={{ controller: annotator }}>
			<div
				ref={boundaryRef}
				className="relative w-full h-full flex-center"
			>
				{image && fitSize ? (
					<AnnotationCanvas
						src={src}
						image={image}
						fitSize={fitSize}
						annotator={annotator}
						svgRef={svgRef}
						containerRef={containerRef}
					/>
				) : (
					<div onClick={(e) => e.stopPropagation()}>
						<img
							src={src}
							alt=""
							className="block select-none"
							style={{ maxHeight: "100%", maxWidth: "100%" }}
						/>
					</div>
				)}
			</div>
			{isEditing && footerSlot && createPortal(<AnnotationToolbar />, footerSlot)}
		</ImageAnnotatorContext.Provider>
	);
}
